/**
 * Blockchair API Service for Bitcoin and Bitcoin Cash
 *
 * Free tier: 1,440 requests/day, 30 requests/minute
 * Documentation: https://blockchair.com/api/docs
 */

const BLOCKCHAIR_API_BASE = 'https://api.blockchair.com';

// Map chain names to Blockchair chain identifiers
const CHAIN_MAP = {
  'bitcoin': 'bitcoin',
  'bitcoincash': 'bitcoin-cash'
};

/**
 * Fetch address data from Blockchair API
 * @param {string} address - Bitcoin or Bitcoin Cash address
 * @param {string} chain - Chain identifier ('bitcoin' or 'bitcoincash')
 * @returns {Promise<object>} Address data including balance, transactions, etc.
 */
export const getBitcoinAddressData = async (address, chain = 'bitcoin') => {
  try {
    const blockchairChain = CHAIN_MAP[chain] || 'bitcoin';
    const url = `${BLOCKCHAIR_API_BASE}/${blockchairChain}/dashboards/address/${address}`;

    const response = await fetch(url);

    if (!response.ok) {
      const errorMsg = `Blockchair API returned ${response.status}`;
      console.warn(errorMsg);
      throw new Error(errorMsg);
    }

    const data = await response.json();

    // Check for API errors (430 = rate limit, 402 = payment required, etc.)
    if (data.context?.code !== 200) {
      if (data.context?.code === 430) {
        console.warn('⚠️ Blockchair API rate limit exceeded (1,440 requests/day limit)');
        console.warn('💡 Falling back to zeros. To get real data, wait a few minutes or use an API key.');
        throw new Error('Blockchair API rate limit exceeded');
      }
      const errorMsg = data.context?.error || `Blockchair API error (code: ${data.context?.code})`;
      throw new Error(errorMsg);
    }

    // Extract address data
    const addressData = data.data?.[address];
    if (!addressData) {
      throw new Error('Address not found in response');
    }

    // Return in Blockchain.com compatible format
    return {
      address: addressData.address?.address || address,
      balance: addressData.address?.balance || 0,
      totalReceived: addressData.address?.received || 0,
      totalSent: addressData.address?.spent || 0,
      transactionCount: addressData.address?.transaction_count || 0,
      transactions: addressData.transactions || []
    };
  } catch (error) {
    console.warn('Blockchair API unavailable:', error.message);
    throw error;
  }
};

/**
 * Format Bitcoin balance from satoshis to BTC
 * @param {number} satoshis - Balance in satoshis
 * @returns {string} Balance in BTC
 */
export const formatBitcoinBalance = (satoshis) => {
  return (satoshis / 100000000).toFixed(8);
};

/**
 * Calculate transaction totals from Blockchair transaction history
 * @param {array} transactions - Transaction history from Blockchair
 * @param {number} balance - Current balance in satoshis
 * @returns {object} Transaction totals
 */
export const calculateBitcoinTransactionTotals = (transactions, balance) => {
  const now = Date.now();
  const oneDayAgo = now - 24 * 60 * 60 * 1000;
  const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;
  const thirtyDaysAgo = now - 30 * 24 * 60 * 60 * 1000;

  let totalSent = 0;
  let totalReceived = balance;

  let txSent1Day = 0, txReceived1Day = 0;
  let txSent7Days = 0, txReceived7Days = 0;
  let txSent30Days = 0, txReceived30Days = 0;

  transactions.forEach(tx => {
    const txTime = new Date(tx.time).getTime();
    const change = tx.balance_change;

    // Accumulate totals
    if (change > 0) {
      totalReceived += change;
    } else if (change < 0) {
      totalSent += Math.abs(change);
    }

    // 1 day totals
    if (txTime >= oneDayAgo) {
      if (change > 0) txReceived1Day += change;
      else if (change < 0) txSent1Day += Math.abs(change);
    }

    // 7 days totals
    if (txTime >= sevenDaysAgo) {
      if (change > 0) txReceived7Days += change;
      else if (change < 0) txSent7Days += Math.abs(change);
    }

    // 30 days totals
    if (txTime >= thirtyDaysAgo) {
      if (change > 0) txReceived30Days += change;
      else if (change < 0) txSent30Days += Math.abs(change);
    }
  });

  return {
    txSent1Day: parseFloat(formatBitcoinBalance(txSent1Day)),
    txReceived1Day: parseFloat(formatBitcoinBalance(txReceived1Day)),
    txSent7Days: parseFloat(formatBitcoinBalance(txSent7Days)),
    txReceived7Days: parseFloat(formatBitcoinBalance(txReceived7Days)),
    txSent30Days: parseFloat(formatBitcoinBalance(txSent30Days)),
    txReceived30Days: parseFloat(formatBitcoinBalance(txReceived30Days)),
    totalSent: parseFloat(formatBitcoinBalance(totalSent)),
    totalReceived: parseFloat(formatBitcoinBalance(totalReceived)),
    totalTransactions: transactions.length
  };
};
